(() => {
  if (customElements.get('cb-advertorial')) return;

  class ChaseBuddyAdvertorial extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const article = this.querySelector('.cb-story, [data-cb-article]');
      if (!article) { this.controller = null; return; }
      // Carry ad attribution (utm_*, fbclid) from this page's URL onto every product link,
      // so the PDP visit and the order still credit the ad that sent the reader here.
      const carry = [...new URLSearchParams(window.location.search)].filter(([key]) => key.startsWith('utm_') || key === 'fbclid');
      if (carry.length) {
        this.querySelectorAll('a[href*="/products/"]').forEach((link) => {
          const url = new URL(link.getAttribute('href'), window.location.origin);
          carry.forEach(([key, value]) => { if (!url.searchParams.has(key)) url.searchParams.set(key, value); });
          link.setAttribute('href', url.origin === window.location.origin ? url.pathname + url.search + url.hash : url.toString());
        });
      }
      const progress = this.querySelector('[data-cb-progress]');
      let scheduled = false;
      this.updateReading = () => {
        scheduled = false;
        const rect = article.getBoundingClientRect();
        const distance = Math.max(1, rect.height - window.innerHeight);
        if (progress) progress.style.transform = `scaleX(${Math.min(1, Math.max(0, -rect.top / distance))})`;
      };
      const schedule = () => {
        if (!scheduled) {
          scheduled = true;
          this.frame = requestAnimationFrame(this.updateReading);
        }
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      this.addEventListener('toggle', schedule, { capture: true, signal });
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(schedule);
        this.observer.observe(article);
      }
      this.querySelectorAll('[data-cb-switcher]').forEach((widget, widgetIndex) => {
        const tablist = widget.querySelector('[data-cb-tabs]');
        const tabs = [...widget.querySelectorAll('[data-cb-tab]')];
        const panels = [...widget.querySelectorAll('[data-cb-panel]')];
        if (!tablist || !tabs.length || tabs.length !== panels.length) return;
        const prefix = `${this.closest('[id]')?.id || 'cb-story'}-compare-${widgetIndex}`;
        tablist.hidden = false;
        tablist.setAttribute('role', 'tablist');
        const activate = (index, focus = false) => {
          tabs.forEach((tab, i) => {
            tab.setAttribute('aria-selected', String(i === index));
            tab.tabIndex = i === index ? 0 : -1;
            panels[i].hidden = i !== index;
          });
          if (focus) tabs[index].focus();
          schedule();
        };
        tabs.forEach((tab, index) => {
          tab.id = `${prefix}-tab-${index}`;
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', `${prefix}-panel-${index}`);
          panels[index].id = `${prefix}-panel-${index}`;
          panels[index].setAttribute('role', 'tabpanel');
          panels[index].setAttribute('aria-labelledby', tab.id);
          panels[index].tabIndex = 0;
          tab.addEventListener('click', () => {
            activate(index);
            this.track('timeline', tab.textContent.trim());
          }, { signal });
          tab.addEventListener('keydown', (event) => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            activate(next, true);
          }, { signal });
        });
        activate(0);
      });
      this.querySelectorAll('[data-cb-checklist]').forEach((checklist) => {
        const inputs = [...checklist.querySelectorAll('input[type="checkbox"]')];
        const result = checklist.querySelector('[data-cb-checklist-result]');
        const output = checklist.querySelector('output');
        if (!result || !output) return;
        result.hidden = false;
        const update = () => {
          const count = inputs.filter((input) => input.checked).length;
          output.textContent = `${count} of ${inputs.length} features checked`;
        };
        checklist.addEventListener('change', () => {
          update();
          this.track('checklist', output.textContent);
        }, { signal });
        update();
      });
      this.querySelectorAll('[data-cb-reel]').forEach((reel) => {
        const video = reel.querySelector('video');
        const play = reel.querySelector('[data-cb-reel-play]');
        if (!video || !play) return;
        const start = () => {
          this.querySelectorAll('[data-cb-reel] video').forEach((other) => {
            if (other !== video && !other.paused) other.pause();
          });
          video.muted = false;
          video.controls = true;
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(() => {
              video.muted = true;
              video.play().catch(() => {});
            });
          }
          this.track('clip', 'play');
        };
        play.addEventListener('click', start, { signal });
        video.addEventListener('play', () => reel.classList.add('is-playing'), { signal });
        video.addEventListener('pause', () => reel.classList.remove('is-playing'), { signal });
        video.addEventListener('ended', () => reel.classList.remove('is-playing'), { signal });
      });
      const loops = [...this.querySelectorAll('[data-cb-autoloop] video[data-src]')];
      if (loops.length) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const load = (video) => {
          if (video.src) return;
          video.src = video.dataset.src;
          if (reduceMotion) {
            video.controls = true;
            video.preload = 'metadata';
          }
        };
        if (!('IntersectionObserver' in window)) {
          loops.forEach(load);
        } else {
          this.loopObserver = new IntersectionObserver((entries) => {
            entries.forEach(({ target: video, isIntersecting }) => {
              if (!isIntersecting) {
                if (!video.paused) video.pause();
                return;
              }
              load(video);
              if (!reduceMotion) video.play().catch(() => {});
            });
          }, { rootMargin: '200px 0px' });
          loops.forEach((video) => this.loopObserver.observe(video));
        }
      }
      // Offer countdown to the visitor's local midnight. Resets every day.
      const countdown = this.querySelector('[data-cb-countdown]');
      if (countdown) {
        const parts = ['h', 'm', 's'].map((unit) => countdown.querySelector(`[data-cb-countdown-${unit}]`));
        const tick = () => {
          const now = new Date();
          const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
          const left = Math.max(0, Math.floor((midnight - now) / 1000));
          [Math.floor(left / 3600), Math.floor(left / 60) % 60, left % 60].forEach((value, i) => {
            if (parts[i]) parts[i].textContent = String(value).padStart(2, '0');
          });
        };
        tick();
        this.countdownTimer = setInterval(tick, 1000);
      }
      this.updateReading();
    }

    track(interaction, value) {
      // A local event hook for the store's analytics integration. No data is sent here.
      this.dispatchEvent(new CustomEvent('cb:story-interaction', {
        bubbles: true,
        detail: { interaction, value, page: window.location.pathname }
      }));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.loopObserver?.disconnect();
      clearInterval(this.countdownTimer);
      cancelAnimationFrame(this.frame);
    }
  }

  customElements.define('cb-advertorial', ChaseBuddyAdvertorial);
})();
